import { useState } from 'react';
import type { IngestRunDto } from '../lib/types';
import { ConfirmDialog } from './Modal';

interface IngestStatusCardProps {
  latestRun: IngestRunDto | null;
  sourceMode: string;
  cadenceMinutes: number;
  onRunNow: () => void;
  isRunning: boolean;
}

const STATUS_STYLES: Record<string, { dot: string; border: string; label: string; text: string }> = {
  Succeeded: { dot: 'bg-emerald-500', border: 'border-emerald-200', label: 'Succeeded', text: 'text-emerald-700' },
  Failed: { dot: 'bg-red-500', border: 'border-red-200', label: 'Failed', text: 'text-red-700' },
  Running: { dot: 'bg-amber-500', border: 'border-amber-200', label: 'Running', text: 'text-amber-700' },
};

const UNKNOWN_STATUS = { dot: 'bg-slate-400', border: 'border-slate-200', label: 'Unknown', text: 'text-slate-600' };

export function IngestStatusCard({ latestRun, sourceMode, cadenceMinutes, onRunNow, isRunning }: IngestStatusCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Defensive: if the server returns a status we don't know (or used to return ints),
  // fall back to the neutral slate styling and a generic "Unknown" label rather than
  // crashing the whole admin screen.
  const style = latestRun ? STATUS_STYLES[latestRun.status] ?? UNKNOWN_STATUS : null;
  const borderClass = style?.border ?? 'border-slate-200';

  return (
    <>
      <div className={`bg-white rounded-lg border p-6 mt-4 ${borderClass}`}>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            {!latestRun ? (
              <p className="text-sm text-slate-500">
                No ingest runs yet. Click Run now to trigger the first run.
              </p>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${style!.dot}`} aria-hidden="true" />
                  <span className={`font-medium text-sm ${style!.text}`}>{style!.label}</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Last run: {new Date(latestRun.startedUtc).toLocaleString()}
                </p>
                <p className="text-sm mt-2 text-brand-charcoal">
                  {latestRun.rowsIn} rows in · {latestRun.rowsLoaded} loaded
                </p>
                {latestRun.status === 'Failed' && latestRun.error && (
                  <p className="text-sm text-red-700 mt-2">{latestRun.error.slice(0, 200)}</p>
                )}
                <div className="flex gap-2 mt-3">
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 font-mono">{sourceMode}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100">Every {cadenceMinutes} min</span>
                </div>
              </>
            )}
            {latestRun && (
              <div className="flex gap-2 mt-3">
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 font-mono">{sourceMode}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100">Every {cadenceMinutes} min</span>
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={isRunning}
            onClick={() => setConfirmOpen(true)}
            className="bg-brand-orange text-white rounded px-4 py-2 text-sm disabled:opacity-60 shrink-0 ml-4"
          >
            {isRunning ? 'Running…' : 'Run now'}
          </button>
        </div>
      </div>

      <div aria-live="polite" className="sr-only">
        {latestRun?.status === 'Succeeded' && 'Ingest run completed. Status: Succeeded.'}
        {latestRun?.status === 'Failed' && 'Ingest run failed. See run history for details.'}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => { setConfirmOpen(false); onRunNow(); }}
        title="Run ingest now?"
        message="This will trigger a manual ingest run immediately. The status card will update when the run completes."
        confirmLabel="Run now"
      />
    </>
  );
}
